import type { LeagueMatchStatus, LeagueOrder, LeaguePageSize, LeagueTab, LeagueUrlState } from './types';

const tabs: readonly LeagueTab[] = ['matches', 'players'];
const sizes: readonly LeaguePageSize[] = [20, 50, 100];
const orders: readonly LeagueOrder[] = ['asc', 'desc'];
const statuses: readonly LeagueMatchStatus[] = ['Fixture', 'Played', 'Postponed', 'Cancelled', 'Suspended', 'Awarded'];
const matchSorts = ['date', 'gameweek', 'home_team', 'away_team', 'total_goals'] as const;
const playerSorts = ['minutes', 'appearances', 'goals', 'assists', 'xg', 'xg_assist', 'shots', 'key_passes', 'name'] as const;

const integer = (value: string | null, min = 1, max = Number.MAX_SAFE_INTEGER): number | null => {
  if (!value || !/^\d+$/.test(value)) return null;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed >= min && parsed <= max ? parsed : null;
};
const dateOnly = (value: string | null): string | null => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const parsed = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value ? value : null;
};
const cleanText = (value: string | null, maximum: number): string | null => {
  const trimmed = value?.trim() ?? '';
  return trimmed && Array.from(trimmed).length <= maximum ? trimmed : null;
};
const defaults = (tab: LeagueTab) => tab === 'matches' ? { sort: 'date', order: 'desc' as const } : { sort: 'minutes', order: 'desc' as const };

export const parseLeagueUrl = (search: URLSearchParams): LeagueUrlState => {
  const rawTab = search.get('tab') as LeagueTab | null;
  const tab = rawTab && tabs.includes(rawTab) ? rawTab : 'matches';
  const allowedSorts: readonly string[] = tab === 'matches' ? matchSorts : playerSorts;
  const fallback = defaults(tab);
  const rawSort = search.get('sort');
  const rawOrder = search.get('order') as LeagueOrder | null;
  let from = dateOnly(search.get('from'));
  let to = dateOnly(search.get('to'));
  if (from && to && from > to) { from = null; to = null; }
  const rawStatus = search.get('status') as LeagueMatchStatus | null;
  const pageSize = integer(search.get('page_size')) as LeaguePageSize | null;
  return {
    tab,
    q: cleanText(search.get('q'), 100) ?? '',
    page: integer(search.get('page')) ?? 1,
    pageSize: pageSize && sizes.includes(pageSize) ? pageSize : 20,
    sort: rawSort && allowedSorts.includes(rawSort) ? rawSort : fallback.sort,
    order: rawOrder && orders.includes(rawOrder) ? rawOrder : fallback.order,
    season: integer(search.get('season')),
    gameweek: tab === 'matches' ? integer(search.get('gameweek'), 0, 100) : null,
    teamId: integer(search.get('team_id')),
    status: tab === 'matches' && rawStatus && statuses.includes(rawStatus) ? rawStatus : null,
    position: tab === 'players' ? cleanText(search.get('position'), 50) : null,
    minMinutes: tab === 'players' ? integer(search.get('min_minutes'), 0, 100000) : null,
    from: tab === 'matches' ? from : null,
    to: tab === 'matches' ? to : null,
  };
};

export const serializeLeagueUrl = (state: LeagueUrlState): URLSearchParams => {
  const params = new URLSearchParams();
  const fallback = defaults(state.tab);
  if (state.tab !== 'matches') params.set('tab', state.tab);
  if (state.q) params.set('q', state.q.trim().slice(0, 100));
  if (state.page > 1) params.set('page', String(state.page));
  if (state.pageSize !== 20) params.set('page_size', String(state.pageSize));
  if (state.sort !== fallback.sort) params.set('sort', state.sort);
  if (state.order !== fallback.order) params.set('order', state.order);
  if (state.season) params.set('season', String(state.season));
  if (state.teamId) params.set('team_id', String(state.teamId));
  if (state.tab === 'matches') {
    if (state.gameweek !== null) params.set('gameweek', String(state.gameweek));
    if (state.status) params.set('status', state.status);
    if (state.from) params.set('from', state.from);
    if (state.to && (!state.from || state.from <= state.to)) params.set('to', state.to);
  } else {
    if (state.position) params.set('position', state.position);
    if (state.minMinutes !== null) params.set('min_minutes', String(state.minMinutes));
  }
  return params;
};

export const parseComparisonUrl = (search: URLSearchParams): { season: number | null; players: number[] } => {
  const season = integer(search.get('season'));
  const players: number[] = [];
  for (const raw of search.getAll('player')) {
    const player = integer(raw);
    if (player !== null && !players.includes(player) && players.length < 4) players.push(player);
  }
  return { season, players };
};
export const serializeComparisonUrl = (season: number | null, players: number[]): URLSearchParams => {
  const params = new URLSearchParams();
  if (season) params.set('season', String(season));
  const seen = new Set<number>();
  players.forEach(player => { if (seen.size < 4 && Number.isSafeInteger(player) && player > 0 && !seen.has(player)) { seen.add(player); params.append('player', String(player)); } });
  return params;
};
